import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  GraduationCap, FileCheck, DollarSign, Home, 
  ArrowRight, CheckCircle2, ChevronDown, Sparkles,
  UserCheck, FileText, Award, Plane, Clock,
  Package, ShoppingCart, Star, Zap, Shield, Globe
} from 'lucide-react';
import { Link } from 'react-router-dom';
import './Services.css';

// Hardcoded, not CMS-fetched — module-scope (not recreated every render)
// and exported so ServicesPage.js can build JSON-LD (FAQPage, Service
// entities) from this exact same content instead of duplicating it.
export const faqs = [
    {
      question: 'What if my GPA is low?',
      answer: 'We have pathway programs and partnerships with universities that accept students with lower GPAs. We also help you strengthen your application through exceptional SOPs, recommendation letters, and relevant work experience. We will tell you honestly which universities fit your grades.'
    },
    {
      question: 'Can I work while studying?',
      answer: 'Yes! In Turkey, international students can work part-time with a work permit where permitted. We help you understand work regulations and can assist with job placement through our network of partner employers.'
    },
    {
      question: 'Do you charge upfront fees?',
      answer: 'Our Partial Scholarships package is free. Our other packages start with a $100 commitment fee, which is part of the package price, not an extra cost, and cannot be refunded. We explain every cost before you pay anything.'
    },
    {
      question: 'How long does the application process take?',
      answer: 'Typically, the complete process from initial consultation to visa approval takes 1-2 months. However, this varies based on the country, university deadlines, and visa processing times. We start early to ensure you meet all deadlines and have the best chance of success.'
    },
    {
      question: 'What makes VisionExploit different from other agencies?',
      answer: 'We offer mentorship-first approach, not just transactional services. With high visa success, real-time application tracking and a team that answers you on WhatsApp, we\'re committed to your success. Our dedicated mentors guide you through every step, ensuring you make informed decisions about your future.'
    },
    {
      question: 'Do you help with accommodation?',
      answer: 'Absolutely! Our post-arrival services include dormitory booking, airport pickup, city orientation, and assistance with finding private accommodation. We ensure you have a smooth transition to your new country and feel at home from day one.'
    }
];

// Also exported for the same JSON-LD reason as `faqs` above.
export const retailServices = [
    {
      id: 1,
      title: 'Turkish Language Crash Course',
      description: '50% discount for VisionExploit clients',
      price: '$99.99'
    },
    {
      id: 2,
      title: 'University Applications',
      description: 'Individual application assistance',
      price: '$29.99'
    },
    {
      id: 3,
      title: 'Travel Itinerary Planning',
      description: 'Customized travel plans and booking assistance',
      price: '$19.99'
    },
    {
      id: 4,
      title: 'Airport Pickup Service',
      description: 'Comfortable and reliable airport transfer',
      price: '$99.99'
    },
    {
      id: 5,
      title: 'Accommodation Assistance',
      description: 'Help finding the perfect place to stay',
      price: '$99.99'
    },
    {
      id: 6,
      title: 'University Registration Assistance',
      description: 'Complete registration support',
      price: '$79.99'
    },
    {
      id: 7,
      title: 'Health Insurance',
      description: 'Student health insurance coverage',
      price: '$19.99'
    },
    {
      id: 8,
      title: 'Residence Permit Assistance',
      description: 'Expert guidance for permit applications',
      price: '$99.99'
    },
    {
      id: 9,
      title: 'Document Authentication',
      description: 'Official document verification and authentication',
      price: '$99.99'
    },
    {
      id: 10,
      title: 'Equivalency Assistance',
      description: 'Degree equivalency evaluation support',
      price: '$109.99'
    },
    {
      id: 11,
      title: 'Job Search Support',
      description: 'Resume building and job placement assistance',
      price: '$69.99'
    }
];

const Services = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const journeyRef = useRef(null);
  const journeyInView = useInView(journeyRef, { once: true, threshold: 0.2 });

  // Journey Steps
  const journeySteps = [
    {
      id: 'discovery',
      title: 'Discovery',
      description: 'Profile Evaluation & Career Counseling',
      icon: UserCheck,
      color: 'blue'
    },
    {
      id: 'application',
      title: 'Application',
      description: 'Document Prep & University Submission',
      icon: FileText,
      color: 'purple'
    },
    {
      id: 'offer',
      title: 'The Offer',
      description: 'Acceptance Letters & Scholarship Negotiation',
      icon: Award,
      color: 'green'
    },
    {
      id: 'visa',
      title: 'The Visa',
      description: 'Embassy Interview Prep',
      icon: FileCheck,
      color: 'orange'
    },
    {
      id: 'departure',
      title: 'Departure',
      description: 'Pre-departure Briefing & Flight Booking',
      icon: Plane,
      color: 'pink'
    }
  ];

  // Extra Services Data
  const extraServices = [
    {
      id: 1,
      title: 'Job Search Support',
      description: 'Resume building, interview prep, and job search strategies for international students.',
      icon: <Zap className="extra-icon" />,
      path: '/job-search-support'
    },
    {
      id: 2,
      title: 'Turkish Language Learning Crash Course',
      description: 'Intensive language course to help you adapt quickly. 50% discount for VisionExploit clients.',
      icon: <Globe className="extra-icon" />,
      path: '/sign-up'
    },
    {
      id: 3,
      title: 'Alumni Network Access',
      description: 'Connect with former students who have successfully studied abroad and build your professional network.',
      icon: <Star className="extra-icon" />,
      path: '/alumni-network'
    },
    {
      id: 4,
      title: 'Emergency Support',
      description: '24/7 assistance for any urgent matters during your studies abroad. We\'re here when you need us.',
      icon: <Shield className="extra-icon" />,
      path: '/emergency-support'
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="services-hub">
      {/* Section 1: The 'Promise' Hero */}
      <section className="services-hero">
        <div className="hero-gradient-bg" />
        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-text"
          >
            <h1 className="hero-headline">
              <span className="hero-headline-main">End-to-End Guidance.</span>
              <br />
              <span className="highlight">From Your First Dream to Your First Class.</span>
            </h1>
            <p className="hero-subtext">
              We don't just process applications. We architect your global career path with precision and care.
            </p>
            <div className="hero-cta-buttons">
              <Link
                to="/sign-up"
                className="cta-primary-btn"
              >
                Get Your Personalized Plan
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/about"
                className="cta-secondary-btn"
              >
                View Our Success Rates
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 2: The 'Bento Grid' Service Menu */}
      <section className="bento-grid-section">
        <div className="bento-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">Our Comprehensive Services</h2>
            <p className="section-subtitle">
              Everything you need for a successful study abroad journey
            </p>
          </motion.div>

          <div className="bento-grid">
            {/* Card 1: Large - University Admissions */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bento-card bento-large"
            >
              <div className="bento-icon-wrapper blue">
                <GraduationCap className="bento-icon" />
              </div>
              <h3 className="bento-title">University Admissions & Strategy</h3>
              <p className="bento-description">
                Personalized shortlisting from hundreds of universities based on your grades and budget.
              </p>
              <ul className="bento-features">
                <li>✓ Profile analysis & university matching</li>
                <li>✓ Application strategy & timeline planning</li>
                <li>✓ SOP & recommendation letter assistance</li>
              </ul>
            </motion.div>

            {/* Card 2: Tall - Visa & Immigration */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bento-card bento-tall"
            >
              <div className="bento-icon-wrapper green">
                <FileCheck className="bento-icon" />
              </div>
              <h3 className="bento-title">Visa & Immigration Support</h3>
              <p className="bento-description">
                <span className="highlight-stat">High visa success.</span> We handle the paperwork, SOPs, and interview prep so you don't stress.
              </p>
              <ul className="bento-features">
                <li>✓ Document preparation & verification</li>
                <li>✓ Interview coaching & mock sessions</li>
                <li>✓ Application tracking & follow-ups</li>
              </ul>
            </motion.div>

            {/* Card 3: Medium - Scholarship */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bento-card bento-medium"
            >
              <div className="bento-icon-wrapper yellow">
                <DollarSign className="bento-icon" />
              </div>
              <h3 className="bento-title">Scholarship & Financial Aid</h3>
              <p className="bento-description">
                <span className="highlight-stat">Scholarships secured</span> for students.
              </p>
              <ul className="bento-features">
                <li>✓ Scholarship search & matching</li>
                <li>✓ Application assistance</li>
                <li>✓ Financial planning guidance</li>
              </ul>
            </motion.div>

            {/* Card 4: Medium - Post-Arrival */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bento-card bento-medium"
            >
              <div className="bento-icon-wrapper purple">
                <Home className="bento-icon" />
              </div>
              <h3 className="bento-title">Post-Arrival & Accommodation</h3>
              <p className="bento-description">
                Dorm booking, airport pickup, and city orientation.
              </p>
              <ul className="bento-features">
                <li>✓ Accommodation assistance</li>
                <li>✓ Airport pickup service</li>
                <li>✓ City & campus orientation</li>
              </ul>
            </motion.div>

            {/* Card 5: Link to Packages */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bento-card bento-medium bento-link-card"
            >
              <div className="bento-icon-wrapper gradient">
                <Package className="bento-icon" />
              </div>
              <h3 className="bento-title">End-to-End Service Packages</h3>
              <p className="bento-description">
                Complete solutions tailored to your needs. Choose from our comprehensive packages.
              </p>
              <Link
                to="/pricing"
                className="bento-link-btn"
              >
                View Packages
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 3: The 'Student Journey' Roadmap */}
      <section ref={journeyRef} className="journey-section">
        <div className="journey-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={journeyInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">Your Journey to Success</h2>
            <p className="section-subtitle">
              A step-by-step roadmap from dream to reality
            </p>
          </motion.div>

          <div className="journey-timeline">
            {journeySteps.map((step, index) => {
              const Icon = step.icon;
              const isLast = index === journeySteps.length - 1;
              
              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={journeyInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  className="journey-step"
                >
                  <div className="journey-step-content">
                    <div className={`journey-icon-wrapper ${step.color}`}>
                      <Icon className="journey-icon" />
                    </div>
                    <div className="journey-step-info">
                      <div className="journey-step-number">Step {index + 1}</div>
                      <h3 className="journey-step-title">{step.title}</h3>
                      <p className="journey-step-description">{step.description}</p>
                    </div>
                  </div>
                  {!isLast && (
                    <motion.div
                      initial={{ scaleY: 0 }}
                      animate={journeyInView ? { scaleY: 1 } : { scaleY: 0 }}
                      transition={{ duration: 0.8, delay: index * 0.2 + 0.3 }}
                      className="journey-connector"
                    />
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 4: 'Vision Elite' Upsell */}
      <section className="elite-section">
        <div className="elite-container">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="elite-card"
          >
            <div className="elite-badge">
              <Sparkles className="elite-badge-icon" />
              <span>Premium Service</span>
            </div>
            <h2 className="elite-title">Vision Elite Service</h2>
            <p className="elite-description">
              For students who want the ultimate advantage. Get dedicated mentorship, unlimited university applications, and priority support.
            </p>
            <ul className="elite-features">
              <li>
                <CheckCircle2 className="elite-check-icon" />
                <span>Dedicated personal mentor</span>
              </li>
              <li>
                <CheckCircle2 className="elite-check-icon" />
                <span>Unlimited university applications</span>
              </li>
              <li>
                <CheckCircle2 className="elite-check-icon" />
                <span>Priority support</span>
              </li>
              <li>
                <CheckCircle2 className="elite-check-icon" />
                <span>Exclusive scholarship opportunities</span>
              </li>
              <li>
                <CheckCircle2 className="elite-check-icon" />
                <span>Guaranteed interview prep sessions</span>
              </li>
            </ul>
            <Link
              to="/sign-up"
              className="elite-cta-btn"
            >
              Upgrade to Vision Elite
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Section 5: Retail Services */}
      <section className="retail-section">
        <div className="retail-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">Retail Services</h2>
            <p className="section-subtitle">
              Individual services available à la carte for your specific needs
            </p>
          </motion.div>

          <div className="retail-grid">
            {retailServices.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="retail-card"
              >
                <div className="retail-card-header">
                  <div className="retail-icon-wrapper">
                    <ShoppingCart className="retail-icon" />
                  </div>
                  <div className="retail-price-badge">
                    {service.price}
                  </div>
                </div>
                <h3 className="retail-title">{service.title}</h3>
                {service.description && (
                  <p className="retail-description">{service.description}</p>
                )}
                <Link
                  to="/sign-up"
                  className="retail-action-btn"
                >
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6: Extra Services */}
      <section className="extra-section">
        <div className="extra-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">Additional Support Services</h2>
            <p className="section-subtitle">
              Extra services to enhance your study abroad experience
            </p>
          </motion.div>

          <div className="extra-grid">
            {extraServices.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="extra-card"
              >
                <div className="extra-icon-wrapper">
                  {service.icon}
                </div>
                <h3 className="extra-title">{service.title}</h3>
                <p className="extra-description">{service.description}</p>
                <Link
                  to={service.path}
                  className="extra-action-btn"
                >
                  Inquire Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7: The 'Problem/Solution' FAQ */}
      <section className="faq-section">
        <div className="faq-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">
              Everything you need to know about our services
            </p>
          </motion.div>

          <div className="faq-accordion">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className={`faq-item ${openFaq === index ? 'open' : ''}`}
              >
                <button
                  className="faq-question"
                  onClick={() => toggleFaq(index)}
                >
                  <span>{faq.question}</span>
                  <ChevronDown 
                    className={`faq-chevron ${openFaq === index ? 'rotated' : ''}`}
                  />
                </button>
                <motion.div
                  initial={false}
                  animate={{
                    height: openFaq === index ? 'auto' : 0,
                    opacity: openFaq === index ? 1 : 0
                  }}
                  transition={{ duration: 0.3 }}
                  className="faq-answer-wrapper"
                >
                  <div className="faq-answer">
                    {faq.answer}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
